import { AsyncPipe, JsonPipe, LowerCasePipe, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  ViewRef,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';
import { GlobalMessageService, GlobalMessageType, Product, ProductService, TranslatePipe } from '@spartacus/core';
import { BREAKPOINT, BreakpointService, CmsComponentData, MediaComponent } from '@spartacus/storefront';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { CUSTOM_LAUNCH_CALLER } from 'src/app/dialog/custom-launch-dialog-config';
import { CheckoutConstants } from 'src/app/model/checkout.model';
import { DealerPrice } from 'src/app/model/dealer-price.model';
import { Category } from 'src/app/model/product.model';
import { CheckoutServiceExtends } from 'src/app/service/checkout/checkout.service';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { DealerPriceService } from 'src/app/service/dealer-price/dealer-price.service';
import { StandardModalService } from 'src/app/service/modal/standard-modal.service';
import { CustomProductService } from 'src/app/service/product/custom-product.service';
import { BIGGER_SKU_IMAGE, SnahCountryService } from 'src/app/service/snah/snah-countries.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { ORDER_FORM, TooltipIconService } from 'src/app/service/tooltip-icon/tooltip-icon.service';
import { CurrentBusinessType } from 'src/app/utility/current-businesstype';
import { DealerPriceCheckerService } from 'src/app/utility/dealer-price-checker.service';
import { ShimanoUtilMethod } from 'src/app/utility/methods.util';
import { RemoveImageTitleAttrDirective } from '../../../../shared/remove-title-attr.directive';
import { ASM_ENABLED_LOCAL_STORAGE_KEY } from '../../asm/asm-constant';
import { TooltipIconComponent } from '../../tooltip-icon/tooltip-icon.component';
import { TooltipTextComponent } from '../../tooltip-text/tooltip-text.component';
export interface OrderFormDetails {
  productCode: string;
  quantity: number;
}

interface ProductExtend extends Product {
  showOrderFormButton?: boolean;
  superCategories?: Category[];
}

@Component({
  selector: 'app-order-form',
  templateUrl: './order-form.component.html',
  styleUrls: ['./order-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    MatIcon,
    MatDivider,
    ReactiveFormsModule,
    NgFor,
    MediaComponent,
    RemoveImageTitleAttrDirective,
    TooltipTextComponent,
    NgTemplateOutlet,
    TooltipIconComponent,
    AsyncPipe,
    LowerCasePipe,
    JsonPipe,
    TranslatePipe,
  ],
})
export class OrderFormComponent implements OnInit, AfterContentChecked, OnDestroy {
  orderForm$: Observable<any> = this.componentData.data$;
  orderFormData$: any;
  baseProductCode: string;
  displayOrderForm = true;
  baseProductSubscription$: Subscription;
  baseProductSubscriptionInit$: Subscription;
  baseProduct$: Observable<ProductExtend>;
  showActiveSnahCountry$: Observable<boolean> = this.SnahCountryService.getSnahFeatures(BIGGER_SKU_IMAGE);
  showActiveSnahCountrySubscription$: Subscription;
  isFishAndSnah: boolean;
  manualAllocationEnabled: boolean = false;

  variantProductCodesList: string[] = [];
  variantProducts: any[] = [];
  variantCategoryCount: number;
  hasDealerPrice = true;
  dealerPriceCheckerSubscription$: Subscription;

  orderFormGroup: FormGroup;
  orderFormDetailsList: OrderFormDetails[] = [];

  showSrp = false;
  srpSubscription$: Subscription;
  dealerPrices: DealerPrice[];
  categoryItems: any[] = [];
  variantElements: any;
  asmEnabled = this.storageService.getLocalStorageItem(ASM_ENABLED_LOCAL_STORAGE_KEY);

  @ViewChild('headerElement', { static: false }) headerElement: ElementRef;
  @ViewChild('bodyElement', { static: false }) bodyElement: ElementRef;
  customHeaderHeight: number[] = [];
  currentBodyHeight: number;

  isMobile = false;
  cxMobileWidth = this.breakpointService.getSize(BREAKPOINT.sm);

  activeCountry$: Observable<string> = this.countryService.getActive();

  tooltipIconConfig$: Observable<any> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) => this.tooltipIconService.getTooltipIconConfig(activeCountry, ORDER_FORM))
  );
  showGstExclusive$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.GST_EXCLUSIVE)
    )
  );
  showGstInclusive$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.GST_INCLUSIVE)
    )
  );
  businessType: string;

  notifyMeProducts: any[] = [];
  notifyMeProductsSubscription: Subscription;
  hasNotifyMe: boolean;

  constructor(
    private componentData: CmsComponentData<any>,
    private route: ActivatedRoute,
    private productService: ProductService,
    private componentService: ComponentService,
    private dealerPriceService: DealerPriceService,
    private cdr: ChangeDetectorRef,
    private breakpointService: BreakpointService,
    private messageService: GlobalMessageService,
    private dealerPriceChecker: DealerPriceCheckerService,
    private countryService: ShimanoCountryService,
    private tooltipIconService: TooltipIconService,
    private checkoutServiceExtends: CheckoutServiceExtends,
    private currentBusinesstype: CurrentBusinessType,
    private storageService: StorageService,
    private SnahCountryService: SnahCountryService,
    protected modalService: StandardModalService,
    private el: ElementRef,
    private vcr: ViewContainerRef,
    private customProductService: CustomProductService
  ) {}

  ngOnInit() {
    this.isMobile = window.innerWidth < this.cxMobileWidth;
    this.showActiveSnahCountrySubscription$ = this.showActiveSnahCountry$.subscribe((isSnahCountry) => {
      this.isFishAndSnah = this.businessType === 'fish' && isSnahCountry;
      this.cdr.detectChanges();
    });
    this.customProductService
      .isProductManualAllocationEnabled$()
      .pipe(take(1))
      .subscribe((isEnabled) => (this.manualAllocationEnabled = isEnabled));

    this.route.params.pipe(take(1)).subscribe((routeParams) => {
      this.baseProduct$ = this.getBaseProductDetails(routeParams.code);
      this.initVariantProductDetails();
    });

    this.baseProductSubscriptionInit$ = this.baseProduct$.subscribe((product) => {
      if (product && product.baseProduct) {
        this.baseProduct$ = this.getBaseProductDetails(product.baseProduct);
        this.initVariantProductDetails();
      }
    });

    this.getInitSrpToggleValue();

    this.srpSubscription$ = this.componentService.srpToggle$.subscribe((checked) => {
      this.showSrp = checked;
      this.updateOrderFormDetails(checked);
      this.cdr.detectChanges();
    });

    this.dealerPriceCheckerSubscription$ = this.componentService.hasDealerPrice$.subscribe((hasDealerPrice) => {
      this.hasDealerPrice = hasDealerPrice;
      if (!(this.cdr as ViewRef).destroyed) {
        this.cdr.detectChanges();
      }
    });
    this.businessType = this.currentBusinesstype.getBusinessType();
  }

  getBaseProductDetails(baseProductCode: string): Observable<ProductExtend> {
    return this.productService.get(baseProductCode);
  }

  initVariantProductDetails() {
    this.baseProductSubscription$ = this.baseProduct$.subscribe((data) => {
      if (data) {
        this.variantCategoryCount = this.getVariantCategoryCount(data.superCategories);
        this.displayOrderForm =
          data.showOrderFormButton &&
          data.hasOwnProperty('variantMatrix') &&
          this.variantCategoryCount > 0 &&
          this.variantCategoryCount <= 3 &&
          !data.hasOwnProperty('baseProduct');
        // Get base product to pass to the breadcrumbs
        if (data.code) {
          this.componentService.setOrderFormProdCategories([{ name: data.name, url: `/product/${data.code}` }]);
        }
        if (this.displayOrderForm) {
          this.variantProductCodesList = [];
          data.variantMatrix.forEach((variant, variantIndex) => {
            if (this.variantCategoryCount === 1) {
              this.variantProductCodesList.push(variant.variantOption.code);
            } else {
              if (this.variantCategoryCount === 3) {
                this.variantElements = variant.elements;
              }
              const variantElem: any[] = [];
              if (variant.elements.length > 0) {
                variant.elements.forEach((variantElement) => {
                  if (this.variantCategoryCount === 2) {
                    this.variantProductCodesList.push(variantElement.variantOption.code);
                  } else if (this.variantCategoryCount === 3) {
                    if (variantElement.elements.length > 0) {
                      variantElement.elements.forEach((subVariantElement) => {
                        this.variantProductCodesList.push(subVariantElement.variantOption.code);
                        const name = subVariantElement.variantValueCategory.name;
                        const checkifExist = variantElem.filter(
                          (data) => data.variantValueCategory.name === name
                        ).length;
                        if (checkifExist <= 0) {
                          variantElem.push(subVariantElement);
                        }
                      });
                    } else if (variant.elements.length > 0) {
                      this.variantCategoryCount = 2;
                      this.variantProductCodesList.push(variantElement.variantOption.code);
                    } else {
                      this.variantCategoryCount = 1;
                      this.variantProductCodesList.push(variant.variantOption.code);
                    }
                  }
                });
              } else {
                this.variantCategoryCount = 1;
                this.variantProductCodesList.push(variant.variantOption.code);
              }

              if (this.variantCategoryCount === 3) {
                this.categoryItems[variantIndex] = variantElem;

                this.categoryItems[variantIndex].forEach((data: any, index: any) => {
                  const name = data.variantValueCategory.name;
                  let items: any[] = [];
                  this.variantElements.forEach((item: any) => {
                    const element = item.elements.filter((data: any) => data.variantValueCategory.name === name);
                    if (element.length > 0) {
                      items.push(element);
                    } else {
                      items.push({});
                    }
                  });
                  this.categoryItems[variantIndex][index] = Object.assign(
                    { subVariantElements: items },
                    this.categoryItems[variantIndex][index]
                  );
                });
              }
            }
          });

          this.getVariantProductDetails();
          this.getDealerPrices(this.variantProductCodesList);
          this.initNotifyMeProducts(this.variantProductCodesList);
        }
      }
      this.cdr.detectChanges();
    });
  }

  getVariantCategoryCount(superCategories: Category[]) {
    return superCategories.filter((superCategory) => superCategory.type === 'VariantCategory').length;
  }

  getVariantProductDetails() {
    this.variantProductCodesList.forEach((code) => {
      this.productService.get(code).subscribe((variantProductDetails) => {
        if (variantProductDetails) {
          this.variantProducts.push(variantProductDetails);
          this.cdr.markForCheck();
        }
      });
    });
    this.createOrderFormGroup();
  }

  getFirstVariantProductCode(code: string): string {
    return this.variantProducts.filter((variantProduct) => variantProduct.code === code).map((res) => res.code)[0];
  }

  getFirstVariantProductName(code: string): string {
    return this.variantProducts
      .filter((variantProduct) => variantProduct.code === code)
      .map((res) => {
        if (res.name) {
          return res.name;
        } else {
          return false;
        }
      })[0];
  }

  getFirstVariantProductImage(code: string): string {
    return this.variantProducts
      .filter((variantProduct) => variantProduct.code === code)
      .map((res) => {
        if (res.images && res.images.PRIMARY) {
          return res.images.PRIMARY.product;
        } else {
          return false;
        }
      })[0];
  }

  getFirstVariantProductImageAltText(code: string): string {
    return this.variantProducts
      .filter((variantProduct) => variantProduct.code === code)
      .map((res) => {
        if (res.images) {
          return res.images.PRIMARY.product.altText;
        } else {
          return false;
        }
      })[0];
  }

  getSrpPriceFormattedValue(code: string): string {
    return this.variantProducts
      .filter((variantProduct) => variantProduct.code === code)
      .map((res) => res.srpPrice && res.srpPrice.formattedValue)[0];
  }

  getSrpPrice(code: string): number {
    return this.variantProducts
      .filter((variantProduct) => variantProduct.code === code)
      .map((res) => (res.srpPrice ? res.srpPrice.value : 0))[0];
  }

  isOutOfStock(code: string): boolean {
    return this.variantProducts
      .filter((variantProduct) => variantProduct.code === code)
      .map((res: any) => {
        return res.stock.stockLevelStatus === 'outOfStock'
          ? true
          : res.stock.stockLevelStatus === 'backorder'
          ? false
          : !res.inStockFlag;
      })[0];
  }

  hasStockLevel(code: string): boolean {
    return (
      this.variantProducts
        .filter((variantProduct) => variantProduct.code === code)
        .map((res) => res.stock.stockLevel)[0] > 0
    );
  }

  getStockLevel(code: string): boolean {
    return this.variantProducts
      .filter((variantProduct) => variantProduct.code === code)
      .map((res) => res.stock.stockLevel)[0];
  }

  isNonSellable(code: string): boolean {
    return (
      this.variantProducts
        .filter((variantProduct) => variantProduct.code === code)
        .map((res) => res.stock.stockLevelStatus)[0] === 'outOfStock'
    );
  }

  isBackOrder(code: string): boolean {
    return this.variantProducts
      .filter((variantProduct) => variantProduct.code === code)
      .map((res: any) => {
        return res?.manualAllocation && this.manualAllocationEnabled
          ? true
          : res.stock.stockLevelStatus === 'backorder' && !res.inStockFlag
          ? true
          : res.stock.stockLevelStatus === 'backorder' && res.inStockFlag
          ? false
          : !res.inStockFlag;
      })[0];
  }

  hasReplacement(code: string): boolean {
    return this.variantProducts
      .filter((variantProduct) => variantProduct.code === code)
      .map((res) => res.hasOwnProperty('replacedBy'))[0];
  }

  createOrderFormGroup() {
    const inputGroup: any = {};
    this.componentService.setOrderFormTotalPrice(0);
    this.componentService.setOrderFormTotalSelected(0);
    this.variantProductCodesList.forEach((productCode) => {
      inputGroup[productCode] = new FormControl('');
    });
    this.orderFormGroup = new FormGroup(inputGroup);
  }

  updateOrderFormDetails(useSrpPrice: boolean) {
    let totalSelected = 0;
    let totalPrice = 0;
    const orderFormDetailsList: OrderFormDetails[] = [];
    const orderFormGtmDataLayer: any[] = [];
    // tslint:disable-next-line: forin
    for (const field in this.orderFormGroup.controls) {
      const inputFieldValue = this.orderFormGroup.controls[field].value;
      if (inputFieldValue !== '') {
        const minOrderQty = this.getMinOrderQuantity(field);
        let newValue = ShimanoUtilMethod.setMinimumOrderQuantity(inputFieldValue, minOrderQty);
        const checkQty = ShimanoUtilMethod.checkUserQuantity(inputFieldValue, minOrderQty);
        if (inputFieldValue === 0 || inputFieldValue === null || isNaN(inputFieldValue)) {
          newValue = 0;
        }
        totalSelected += newValue;
        totalPrice += newValue * (useSrpPrice ? this.getSrpPrice(field) : this.dealerPriceValue(field));
        if (newValue > 0) {
          orderFormDetailsList.push({ productCode: field, quantity: newValue });
          orderFormGtmDataLayer.push({
            id: field,
            name: this.getFirstVariantProductName(field),
            price: this.dealerPriceValue(field),
            srpPrice: this.getSrpPrice(field),
            quantity: newValue,
          });
        }
        this.orderFormGroup.controls[field].setValue(newValue);

        if (checkQty > 0) {
          this.messageService.add(
            { key: 'moq.orderFormMessage', params: { value: newValue, moq: minOrderQty } },
            GlobalMessageType.MSG_TYPE_CONFIRMATION
          );
        }
      }
    }
    this.componentService.setOrderFormTotalSelected(totalSelected);
    this.componentService.setOrderFormTotalPrice(totalPrice);
    this.componentService.setOrderFormDetailsList(orderFormDetailsList);
    this.componentService.setOrderFormGtmDataLayer(orderFormGtmDataLayer);
  }

  getMinOrderQuantity(code: string): string {
    return this.variantProducts
      .filter((variantProduct) => variantProduct.code === code)
      .map((res) => {
        if (res.minOrderQuantity) {
          return res.minOrderQuantity;
        } else {
          return 1;
        }
      })[0];
  }

  getDealerPrices(productCodes: string[]) {
    this.dealerPriceService
      .getDealerPricesByProductCodes(productCodes)
      .pipe(take(1))
      .subscribe((data) => {
        this.dealerPrices = data.dealerPrices;
        this.cdr.detectChanges();
      });
  }

  dealerPrice(productCode: string): string {
    if (this.dealerPrices) {
      return this.dealerPriceService.getDealerPriceByProductCode(this.dealerPrices, productCode);
    }
  }

  dealerPriceValue(productCode: string): number {
    if (this.dealerPrices) {
      return this.dealerPriceService.getDealerPriceValueByProductCode(this.dealerPrices, productCode);
    }
  }

  getInitSrpToggleValue() {
    this.showSrp = this.storageService.getLocalStorageItem('showSrp') === 'true';
  }

  numberOnly(event: any): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  getImageRendition(image: any) {
    let size: string;
    size = ShimanoUtilMethod.imageRendition(80);
    return ShimanoUtilMethod.newImageFormat(image, size);
  }

  setCustomHeaderHeight(element: any, variantIndex: number): string {
    this.customHeaderHeight[variantIndex] = element.offsetHeight;
    return '';
  }

  isOneWord(name: string): boolean {
    return name.trim().indexOf(' ') === -1;
  }

  initNotifyMeProducts(productCodeList: any[]) {
    this.hasNotifyMe = false;
    this.notifyMeProducts = [];
    this.notifyMeProductsSubscription?.unsubscribe();

    const observables = productCodeList.map((code) =>
      this.getBaseProductDetails(code).pipe(
        catchError(() => of(null)),
        filter(Boolean),
        map((product) => (product?.hasOwnProperty('notifyMe') ? product : null))
      )
    );

    this.notifyMeProductsSubscription = combineLatest(observables)
      .pipe(
        take(1),
        tap((results) => {
          this.notifyMeProducts = results?.filter(Boolean);
          this.hasNotifyMe = this.notifyMeProducts?.length > 0;
        })
      )
      .subscribe();
  }
  productImageModal(code: any) {
    if (this.isFishAndSnah) {
      this.modalService
        .openDialog(CUSTOM_LAUNCH_CALLER.ORDER_FORM_PRODUCT_IMAGE, this.el, this.vcr, {
          modalData: this.getFirstVariantProductImage(code),
        })
        ?.pipe(take(1))
        .subscribe();
    }
  }

  notifyMeModal() {
    this.modalService
      .openDialog(CUSTOM_LAUNCH_CALLER.NOTIFY_ME_MODAL, this.el, this.vcr, {
        products: this.notifyMeProducts,
      })
      ?.pipe(take(1))
      .subscribe();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < this.cxMobileWidth;
  }

  ngAfterContentChecked() {
    if (this.bodyElement) {
      this.currentBodyHeight = this.bodyElement.nativeElement.offsetHeight;
    }
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    if (this.srpSubscription$) {
      this.srpSubscription$.unsubscribe();
    }
    if (this.baseProductSubscription$) {
      this.baseProductSubscription$.unsubscribe();
    }
    if (this.baseProductSubscriptionInit$) {
      this.baseProductSubscriptionInit$.unsubscribe();
    }
    if (this.dealerPriceCheckerSubscription$) {
      this.dealerPriceCheckerSubscription$.unsubscribe();
    }
    if (this.notifyMeProductsSubscription) {
      this.notifyMeProductsSubscription.unsubscribe();
    }
    if (this.showActiveSnahCountrySubscription$) {
      this.showActiveSnahCountrySubscription$.unsubscribe();
    }
  }
}
